import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import {
	mdiAccountHeart,
	mdiChat,
	mdiDotsVertical,
	mdiCalendarClock,
	mdiClipboardPulse,
	mdiPill,
	mdiArrowLeft,
	mdiFileDocument,
	mdiHistory,
	mdiNoteText,
} from "@mdi/js";
import {
	CircularProgress,
	IconButton,
	Chip,
	Button,
	Paper,
} from "@mui/material";
import { ListSidebar, MainLayout, useMobileLayout } from "layouts";
import { Chat } from "components";
import { useAccount } from "shared/hooks";
import { clsx } from "@ismael1361/utils";
import {
	MedPatientServices,
	NotistackServices,
} from "shared/services";
import type {
	MedPatientAgentDTO,
	MedPatientMessageDTO,
	MedAppointmentDTO,
	MedPrescriptionDTO,
	MedHealthRecordDTO,
	MedPatientAppointmentDetailDTO,
} from "shared/services/MedPatientServices";
import styles from "./styles.module.scss";

// ── Types ─────────────────────────────────────────────────────────
type SidebarSection = "chat" | "appointments" | "prescriptions" | "history";

// ── Main Component ────────────────────────────────────────────────

export const MedPatientPage: React.FC = () => {
	const user = useAccount();
	const { isMobile, showContent, showSidebar, openDrawer } = useMobileLayout();

	// ── Data ──────────────────────────────────────────────────────
	const [agents, setAgents] = useState<MedPatientAgentDTO[]>([]);
	const [appointments, setAppointments] = useState<MedAppointmentDTO[]>([]);
	const [prescriptions, setPrescriptions] = useState<MedPrescriptionDTO[]>([]);
	const [healthRecords, setHealthRecords] = useState<MedHealthRecordDTO[]>([]);
	const [loadingData, setLoadingData] = useState(true);

	// ── Navigation ────────────────────────────────────────────────
	const [activeSection, setActiveSection] = useState<SidebarSection>("chat");
	const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

	// ── Appointment Detail ────────────────────────────────────────
	const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
	const [appointmentDetail, setAppointmentDetail] = useState<MedPatientAppointmentDetailDTO | null>(null);
	const [loadingDetail, setLoadingDetail] = useState(false);

	// ── Chat ──────────────────────────────────────────────────────
	const [messages, setMessages] = useState<MedPatientMessageDTO[]>([]);
	const [loadingMessages, setLoadingMessages] = useState(false);
	const [sendingMessage, setSendingMessage] = useState(false);

	// ── Active agent ──────────────────────────────────────────────
	const activeAgent = useMemo(() => {
		if (!selectedAgentId) return agents[0] || null;
		return agents.find(a => a.id === selectedAgentId) || null;
	}, [agents, selectedAgentId]);

	// ── Load Data ─────────────────────────────────────────────────
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				setLoadingData(true);
				const [ags, appts, presc, recs] = await Promise.all([
					MedPatientServices.listAgents(),
					MedPatientServices.listAppointments(),
					MedPatientServices.listPrescriptions(),
					MedPatientServices.listHealthRecords(),
				]);
				if (!cancelled) {
					setAgents(ags);
					setAppointments(appts);
					setPrescriptions(presc);
					setHealthRecords(recs);
					if (ags.length > 0 && !selectedAgentId) {
						setSelectedAgentId(ags[0].id);
					}
				}
			} catch (err: any) {
				NotistackServices.showErrorMessage(err?.message || "Erro ao carregar dados");
			} finally {
				if (!cancelled) setLoadingData(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	// ── Load Conversation ─────────────────────────────────────────
	useEffect(() => {
		if (!activeAgent) return;
		let cancelled = false;
		(async () => {
			try {
				setLoadingMessages(true);
				const conv = await MedPatientServices.getConversation(activeAgent.id);
				if (!cancelled) setMessages(conv.messages || []);
			} catch {
				if (!cancelled) setMessages([]);
			} finally {
				if (!cancelled) setLoadingMessages(false);
			}
		})();
		return () => { cancelled = true; };
	}, [activeAgent?.id]);

	// ── Send Message ──────────────────────────────────────────────
	const handleSendMessage = useCallback(async (content: string) => {
		if (!activeAgent || sendingMessage) return;
		try {
			setSendingMessage(true);
			const result = await MedPatientServices.sendMessage(activeAgent.id, content);
			setMessages(prev => [...prev, result.userMessage, result.assistantMessage]);
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao enviar mensagem");
		} finally {
			setSendingMessage(false);
		}
	}, [activeAgent, sendingMessage]);

	// ── Load Appointment Detail ──────────────────────────────────
	const loadAppointmentDetail = useCallback(async (appointmentId: string) => {
		try {
			setLoadingDetail(true);
			setSelectedAppointmentId(appointmentId);
			const detail = await MedPatientServices.getAppointmentDetail(appointmentId);
			setAppointmentDetail(detail);
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao carregar detalhes da consulta");
		} finally {
			setLoadingDetail(false);
		}
	}, []);

	// ── Loading State ─────────────────────────────────────────────
	if (loadingData) {
		return (
			<MainLayout>
				<div className={styles.emptyState}>
					<CircularProgress size={32} />
					<p>Carregando portal do paciente…</p>
				</div>
			</MainLayout>
		);
	}

	// ── Chat Messages Formatting ──────────────────────────────────
	const chatMessages = messages.map(m => ({
		id: m.id,
		role: m.role as "user" | "assistant",
		content: m.content,
		createdAt: m.createdAt,
	}));

	// ── Sidebar ───────────────────────────────────────────────────
	const sidebar = (
		<ListSidebar>
			<div className={styles.sidebarHeader}>
				<span className={styles.sidebarTitle}>Portal do Paciente</span>
			</div>

			<div className={styles.sidebarSection}>
				<h3>Navegação</h3>
				{[
					{ key: "chat" as SidebarSection, icon: mdiChat, label: "Chat com IA" },
					{ key: "appointments" as SidebarSection, icon: mdiCalendarClock, label: "Minhas Consultas" },
					{ key: "prescriptions" as SidebarSection, icon: mdiPill, label: "Receitas" },
					{ key: "history" as SidebarSection, icon: mdiHistory, label: "Histórico" },
				].map(item => (
					<div
						key={item.key}
						className={clsx(styles.navItem, activeSection === item.key && styles.navItemActive)}
						onClick={() => setActiveSection(item.key)}
					>
						<Icon path={item.icon} size={0.85} />
						<span>{item.label}</span>
					</div>
				))}
			</div>

			{activeSection === "chat" && (
				<div className={styles.sidebarSection}>
					<h3>Assistentes Disponíveis</h3>
					{loadingData ? (
						<div className={styles.emptySidebar}><CircularProgress size={20} /></div>
					) : agents.length === 0 ? (
						<div className={styles.emptySidebar}>
							<p>Nenhum assistente disponível</p>
						</div>
					) : (
						agents.map(agent => (
							<div
								key={agent.id}
								className={clsx(styles.subjectCard, activeAgent?.id === agent.id && styles.subjectCardActive)}
								onClick={() => {
									setSelectedAgentId(agent.id);
									if (isMobile) showContent();
								}}
							>
								<div className={styles.subjectIcon}>
									{agent.agentType === "nurse" ? "👩‍⚕️" : "🤖"}
								</div>
								<div className={styles.subjectInfo}>
									<span className={styles.subjectName}>{agent.name}</span>
									<span className={styles.subjectDesc}>{agent.description || "Assistente IA"}</span>
								</div>
							</div>
						))
					)}
				</div>
			)}

			{activeSection === "appointments" && (
				<div className={styles.sidebarSection}>
					<h3>Próximas Consultas</h3>
					{appointments.length === 0 ? (
						<div className={styles.emptySidebar}>
							<p>Nenhuma consulta agendada</p>
						</div>
					) : (
						appointments.map(appt => (
							<div key={appt.id} className={styles.subjectCard}>
								<div className={styles.subjectIcon}>📅</div>
								<div className={styles.subjectInfo}>
									<span className={styles.subjectName}>{appt.title || "Consulta"}</span>
									<span className={styles.subjectDesc}>{new Date(appt.scheduledAt).toLocaleDateString("pt-BR")}</span>
								</div>
							</div>
						))
					)}
				</div>
			)}

			{activeSection === "prescriptions" && (
				<div className={styles.sidebarSection}>
					<h3>Receitas Ativas</h3>
					{prescriptions.length === 0 ? (
						<div className={styles.emptySidebar}>
							<p>Nenhuma receita ativa</p>
						</div>
					) : (
						prescriptions.map(presc => (
							<div key={presc.id} className={styles.subjectCard}>
								<div className={styles.subjectIcon}>💊</div>
								<div className={styles.subjectInfo}>
									<span className={styles.subjectName}>{presc.title || "Receita"}</span>
									<span className={styles.subjectDesc}>{new Date(presc.createdAt).toLocaleDateString("pt-BR")}</span>
								</div>
							</div>
						))
					)}
				</div>
			)}
		</ListSidebar>
	);

	// ── Main Content ──────────────────────────────────────────────
	const renderMainContent = () => {
		// ── Appointments View ─────────────────────────────────────
		if (activeSection === "appointments") {
			// ── Appointment Detail ────────────────────────────────
			if (selectedAppointmentId && appointmentDetail) {
				const transcriptions = appointmentDetail.records.filter((r: any) => r.recordType === "transcription");
				const otherRecords = appointmentDetail.records.filter((r: any) => r.recordType !== "transcription");

				return (
					<div className={styles.sectionContent}>
						<div className={styles.sectionContentHeader}>
							<Button
								startIcon={<Icon path={mdiArrowLeft} size={0.8} />}
								onClick={() => { setSelectedAppointmentId(null); setAppointmentDetail(null); }}
								size="small"
							>
								Voltar
							</Button>
							<h2>📅 {appointmentDetail.appointment.title || "Consulta"}</h2>
						</div>

						<Paper elevation={1} sx={{ p: 2, mb: 2 }}>
							<div className={styles.dataCardBody}>
								<div className={styles.dataCardRow}>
									<span className={styles.dataCardLabel}>Médico(a)</span>
									<span>{appointmentDetail.doctorName}</span>
								</div>
								<div className={styles.dataCardRow}>
									<span className={styles.dataCardLabel}>Data</span>
									<span>{new Date(appointmentDetail.appointment.scheduledAt).toLocaleDateString("pt-BR")} às {new Date(appointmentDetail.appointment.scheduledAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
								</div>
								<div className={styles.dataCardRow}>
									<span className={styles.dataCardLabel}>Status</span>
									<Chip
										size="small"
										label={appointmentDetail.appointment.status === "scheduled" ? "Agendada" : appointmentDetail.appointment.status === "completed" ? "Concluída" : appointmentDetail.appointment.status}
										color={appointmentDetail.appointment.status === "completed" ? "success" : "primary"}
									/>
								</div>
								{appointmentDetail.appointment.notes && (
									<div className={styles.dataCardRow}>
										<span className={styles.dataCardLabel}>Observações</span>
										<span>{appointmentDetail.appointment.notes}</span>
									</div>
								)}
							</div>
						</Paper>

						{/* Transcrições */}
						<h3 style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0 8px" }}>
							<Icon path={mdiNoteText} size={0.9} /> Transcrições ({transcriptions.length})
						</h3>
						{transcriptions.length === 0 ? (
							<Paper elevation={0} sx={{ p: 2, bgcolor: "action.hover", textAlign: "center", color: "text.secondary" }}>
								Nenhuma transcrição registrada para esta consulta.
							</Paper>
						) : (
							transcriptions.map((t: any) => (
								<Paper key={t.id} elevation={1} sx={{ p: 2, mb: 1, whiteSpace: "pre-wrap", fontSize: "0.875rem", fontFamily: "monospace" }}>
									<div style={{ fontSize: "0.75rem", color: "#999", marginBottom: 4 }}>
										{new Date(t.createdAt).toLocaleString("pt-BR")}
									</div>
									{t.content}
								</Paper>
							))
						)}

						{/* Outros Registros */}
						{otherRecords.length > 0 && (
							<>
								<h3 style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0 8px" }}>
									<Icon path={mdiFileDocument} size={0.9} /> Outros Registros ({otherRecords.length})
								</h3>
								{otherRecords.map((r: any) => (
									<Paper key={r.id} elevation={1} sx={{ p: 2, mb: 1 }}>
										<strong>{r.title || r.recordType}</strong>
										<p style={{ margin: "4px 0 0" }}>{r.content}</p>
									</Paper>
								))}
							</>
						)}
					</div>
				);
			}

			if (loadingDetail) {
				return (
					<div className={styles.emptyState}>
						<CircularProgress size={32} />
						<p>Carregando detalhes da consulta…</p>
					</div>
				);
			}

			return (
				<div className={styles.sectionContent}>
					<div className={styles.sectionContentHeader}>
						<h2>📅 Minhas Consultas</h2>
					</div>
					{appointments.length === 0 ? (
						<div className={styles.emptySection}>
							<Icon path={mdiCalendarClock} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.25 }} />
							<h3>Nenhuma consulta agendada</h3>
							<p>Suas consultas aparecerão aqui quando forem marcadas pelo seu médico.</p>
						</div>
					) : (
						<div className={styles.dataGrid}>
							{appointments.sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()).map(appt => (
								<div key={appt.id} className={styles.dataCard} style={{ cursor: "pointer" }} onClick={() => loadAppointmentDetail(appt.id)}>
									<div className={styles.dataCardHeader}>
										<span className={styles.dataCardTitle}>{appt.title || "Consulta"}</span>
										<Chip
											size="small"
											label={appt.status === "scheduled" ? "Agendada" : appt.status === "completed" ? "Concluída" : appt.status === "cancelled" ? "Cancelada" : appt.status}
											color={appt.status === "scheduled" ? "primary" : appt.status === "completed" ? "success" : "default"}
										/>
									</div>
									<div className={styles.dataCardBody}>
										<div className={styles.dataCardRow}>
											<span className={styles.dataCardLabel}>Data</span>
											<span>{new Date(appt.scheduledAt).toLocaleDateString("pt-BR")} às {new Date(appt.scheduledAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
										</div>
										<div className={styles.dataCardRow}>
											<span className={styles.dataCardLabel}>Duração</span>
											<span>{appt.durationMinutes || 30} min</span>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			);
		}

		// ── Prescriptions View ────────────────────────────────────
		if (activeSection === "prescriptions") {
			return (
				<div className={styles.sectionContent}>
					<div className={styles.sectionContentHeader}>
						<h2>💊 Minhas Receitas</h2>
					</div>
					{prescriptions.length === 0 ? (
						<div className={styles.emptySection}>
							<Icon path={mdiPill} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.25 }} />
							<h3>Nenhuma receita ativa</h3>
							<p>Suas receitas aparecerão aqui quando prescritas pelo seu médico.</p>
						</div>
					) : (
						<div className={styles.dataGrid}>
							{prescriptions.map(presc => (
								<div key={presc.id} className={styles.dataCard}>
									<div className={styles.dataCardHeader}>
										<span className={styles.dataCardTitle}>💊 {presc.title || "Receita"}</span>
										<span className={styles.dataCardDate}>{new Date(presc.createdAt).toLocaleDateString("pt-BR")}</span>
									</div>
									<div className={styles.dataCardBody}>
										{presc.content && (
											<p className={styles.dataCardText}>{presc.content}</p>
										)}
										{presc.structuredData && (
											<>
												{presc.structuredData.medication && (
													<div className={styles.dataCardRow}>
														<span className={styles.dataCardLabel}>Medicamento</span>
														<span>{presc.structuredData.medication}</span>
													</div>
												)}
												{presc.structuredData.dosage && (
													<div className={styles.dataCardRow}>
														<span className={styles.dataCardLabel}>Dosagem</span>
														<span>{presc.structuredData.dosage}</span>
													</div>
												)}
												{presc.structuredData.frequency && (
													<div className={styles.dataCardRow}>
														<span className={styles.dataCardLabel}>Frequência</span>
														<span>{presc.structuredData.frequency}</span>
													</div>
												)}
											</>
										)}
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			);
		}

		// ── History View ──────────────────────────────────────────
		if (activeSection === "history") {
			return (
				<div className={styles.sectionContent}>
					<div className={styles.sectionContentHeader}>
						<h2>📋 Histórico de Saúde</h2>
					</div>
					{healthRecords.length === 0 ? (
						<div className={styles.emptySection}>
							<Icon path={mdiHistory} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.25 }} />
							<h3>Nenhum registro encontrado</h3>
							<p>Seu histórico médico aparecerá aqui conforme seus atendimentos forem realizados.</p>
						</div>
					) : (
						<div className={styles.dataGrid}>
							{healthRecords.map(rec => (
								<div key={rec.id} className={styles.dataCard}>
									<div className={styles.dataCardHeader}>
										<span className={styles.dataCardTitle}>
											{rec.recordType === "consultation" ? "🩺" : rec.recordType === "prescription" ? "💊" : rec.recordType === "exam" ? "🔬" : "📄"} {rec.title || rec.recordType || "Registro"}
										</span>
										<span className={styles.dataCardDate}>{new Date(rec.createdAt).toLocaleDateString("pt-BR")}</span>
									</div>
									<div className={styles.dataCardBody}>
										{rec.content && <p className={styles.dataCardText}>{rec.content}</p>}
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			);
		}

		// ── Default: Chat ─────────────────────────────────────────
		return (
			<>
				<div className={styles.chatHeader}>
					{isMobile && (
						<button className={styles.mobileBackBtn} onClick={showSidebar}>
							<Icon path={mdiArrowLeft} size={0.9} />
						</button>
					)}
					<div className={styles.chatHeaderAvatar}>
						<span>{activeAgent?.agentType === "nurse" ? "👩‍⚕️" : "🤖"}</span>
					</div>
					<div className={styles.chatHeaderInfo}>
						<span className={styles.chatHeaderName}>{activeAgent?.name || "IA Enfermeira"}</span>
						<span className={styles.chatHeaderDesc}>{activeAgent?.description || "Assistente médico IA para pacientes"}</span>
					</div>
					<div className={styles.chatHeaderActions}>
						{isMobile && (
							<IconButton size="small" className={styles.chatHeaderMenuBtn} onClick={openDrawer}>
								<Icon path={mdiDotsVertical} size={0.9} />
							</IconButton>
						)}
					</div>
				</div>
				{activeAgent ? (
					<Chat
						onSend={handleSendMessage}
						disabled={sendingMessage}
						placeholder="Descreva seus sintomas ou tire sua dúvida..."
					>
						{loadingMessages && (
							<div className={styles.emptyChat}>
								<CircularProgress size={28} />
								<p>Carregando mensagens…</p>
							</div>
						)}
						{!loadingMessages && messages.length === 0 && (
							<div className={styles.emptyChat}>
								<p>Envie uma mensagem para iniciar a conversa.</p>
							</div>
						)}
						{messages.map((msg) => {
							const outgoing = msg.role === "user";
							return (
								<Chat.Message
									key={msg.id}
									id={msg.id}
									incoming={!outgoing}
									outgoing={outgoing}
									senderName={outgoing ? (user?.firstName || "Você") : (activeAgent?.name || "IA")}
								>
									{msg.content}
								</Chat.Message>
							);
						})}
					</Chat>
				) : (
					<div className={styles.emptyChat}>
						<Icon path={mdiAccountHeart} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.3 }} />
						<h3>Assistente IA de Saúde</h3>
						<p>Converse com nossa IA para tirar dúvidas, fazer triagem ou acompanhamento pós-consulta.</p>
					</div>
				)}
			</>
		);
	};

	const mainContent = renderMainContent();

	return (
		<MainLayout sidebar={sidebar}>
			{mainContent}
		</MainLayout>
	);
};
